import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Button,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField as MuiTextField,
  Theme,
  Typography,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import _ from "lodash";
import {
  IVacationBidCrew,
  IVacationBidCrewImportRow,
  IVacationBidCrewImportRowPreview,
  IVacationBidCrewListItem,
  VacationBidCrewImportOutcome,
} from "@vmosolutions/oc-interfaces";

// Components
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { VacationCrewTable } from "./VacationCrewTable";
import { ImportCsvDialog } from "../Csv/ImportCsvDialog";
import { INotifications } from "src/API/AmplifyAPIWrapper";
import { ICsvIssue, parseCsvFile } from "src/utils/csvFile";
import { crewFromCsv, crewToCsv } from "../Csv/crewCsv";
import { download } from "src/utils/downloadTextFile";

// Redux
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import { getCrew } from "src/redux/actions/CrewSchedule/getCrew";
import { getCrewGroups, gotCrewAction } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { openSnackbar } from "src/redux/actions/App.Actions";

import { IVacationCrewTabProps } from "../types";
import { VACATION_BID_TEST_ID } from "../testIds";
import { awardAdjustmentsLine, awardChangesSnackbar } from "../Awarding/awardingHelpers";
import {
  addVacationBidCrew,
  buildVacationBidCrew,
  getVacationBidCrew,
  importVacationBidCrew,
  previewVacationBidCrewImport,
  removeVacationBidCrew,
  vacationCsvFileName,
} from "../helpers";

/** Why a row the server resolved cannot be applied, phrased for the admin. */
const IMPORT_OUTCOME_REASON: Partial<Record<VacationBidCrewImportOutcome, string>> = {
  [VacationBidCrewImportOutcome.Unmatched]: "no crew member carries this employee ID.",
  [VacationBidCrewImportOutcome.Inactive]: "this crew member is not active and cannot bid.",
  [VacationBidCrewImportOutcome.Ambiguous]: "more than one crew member carries this employee ID.",
};

export interface IGroupOption {
  value: string;
  key: string;
  label: string;
}

const VacationCrewGroupsFieldStyles = (theme: Theme): any => ({
  root: {
    height: "100%",
    width: "100%",
    position: "relative",
    "& .crew_warning": {
      padding: "4px 15px 8px",
      "& .crew_mismatch_warning": { margin: "0 0 0 5px" },
    },
    "& .crew_actions": {
      marginBottom: "-10px",
      paddingLeft: "15px",
      "& .import_csv_btn, & .build_crew_btn, & .add_crew_btn": {
        padding: "2px 8px",
        margin: "5px 5px",
        fontSize: 12,
        height: "24px",
        whiteSpace: "nowrap",
        // Never squeezed under their own text, whatever else shares the row.
        flexShrink: 0,
        color: theme.custom.forms.labels,
        borderColor: theme.custom.forms.inputBorder,
      },
      // Wide enough for "Lastname, Firstname (employeeId)" and its floating label, neither of
      // which fit once the row's buttons have taken their share.
      "& .add_crew_select": {
        width: 280,
        minWidth: 280,
        flexShrink: 0,
        margin: "0 5px",
        "& .MuiInputBase-root": { fontSize: 12 },
        "& .MuiFormLabel-root": { fontSize: 12, color: theme.custom.forms.labels },
      },
    },
  },
});

const toGroupOption = (group: string): IGroupOption => ({ value: group, key: group, label: group });

const sortedCopy = (groups: string[] | undefined): string[] => [...(groups ?? [])].sort();

/**
 * Crew groups field for the vacation bid package editor. Selecting groups previews which crew a
 * rebuild would add or remove; the package crew themselves are fetched from the package.
 */
export const VacationCrewGroupsField = (props: IVacationCrewTabProps): JSX.Element => {
  const { id, path, mosaicPane, packageId, packageTitle, crewGroups, initialCrewGroups, crewVersion } = props;
  const dispatch = useDispatch();

  const isMounted = useRef<boolean>(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // LOAD CREW MEMBERS
  const crew: ICrewMember[] = useSelector((state: IRootState) => state.crewSchedule.crewMembers);
  const [loadingCrew, setLoadingCrew] = useState<boolean>(false);
  // Whether a crew fetch has come back. Held apart from the list itself because an empty list is a
  // real answer, and treating it as "still loading" would disable the controls for good.
  const [crewFetched, setCrewFetched] = useState<boolean>(false);

  const getCrewAction = useCallback(async (): Promise<ICrewMember[]> => {
    setLoadingCrew(true);
    // Null marks a fetch that failed, which must leave the store as it was. An empty array is a
    // real answer and replaces the store, so a stale crew list cannot outlive it.
    let fetchedCrew: ICrewMember[] | null = null;
    try {
      const result = await getCrew();
      fetchedCrew = Array.isArray(result) ? result : [];
    } catch {
      fetchedCrew = null;
    }
    if (isMounted.current) {
      if (fetchedCrew) {
        dispatch(gotCrewAction(fetchedCrew));
        setCrewFetched(true);
      }
      setLoadingCrew(false);
    }
    return fetchedCrew ?? [];
  }, [dispatch]);

  // Fetch the crew list once when the store has none.
  const crewRequested = useRef<boolean>(false);
  useEffect(() => {
    if (!crew?.length && !crewRequested.current) {
      crewRequested.current = true;
      void getCrewAction();
    }
  }, [crew, getCrewAction]);

  // LOAD CREW GROUPS
  const [crewGroupOptions, setCrewGroupOptions] = useState<string[]>([]);
  const getCrewGroupsAction = useCallback(async (): Promise<void> => {
    let groups: string[] = [];
    try {
      groups = await getCrewGroups();
    } catch {
      groups = [];
    }
    if (isMounted.current) setCrewGroupOptions(Array.isArray(groups) ? groups : []);
  }, []);
  useEffect(() => {
    void getCrewGroupsAction();
  }, [getCrewGroupsAction]);

  const groupOptions: IGroupOption[] = useMemo(
    () =>
      (crewGroupOptions ?? [])
        .map(toGroupOption)
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())),
    [crewGroupOptions]
  );

  // LOAD PACKAGE CREW
  const [packageCrew, setPackageCrew] = useState<IVacationBidCrewListItem[]>([]);
  const [loadingPackageCrew, setLoadingPackageCrew] = useState<boolean>(false);

  const fetchPackageCrew = useCallback(async (): Promise<void> => {
    if (!packageId) {
      setPackageCrew([]);
      return;
    }
    setLoadingPackageCrew(true);
    try {
      const fetched: IVacationBidCrewListItem[] = await getVacationBidCrew(packageId);
      if (!isMounted.current) return;
      setPackageCrew(Array.isArray(fetched) ? fetched : []);
    } finally {
      // The grid stays under its loading overlay unless this runs on the throwing path too.
      if (isMounted.current) {
        setLoadingPackageCrew(false);
      }
    }
  }, [packageId]);

  // Refetch when the package changes and after every save.
  useEffect(() => {
    void fetchPackageCrew();
  }, [fetchPackageCrew, crewVersion]);

  // A saved entitlement replaces the day counts on its row.
  const handleRowUpdated = useCallback((updated: IVacationBidCrew) => {
    const userId = String(updated.userId);
    setPackageCrew((previous) =>
      previous.map((row) =>
        String(row.userId) === userId ? { ...row, vacationDays: updated.vacationDays, statDays: updated.statDays } : row
      )
    );
  }, []);

  const handleRefresh = useCallback((): void => {
    // Groups and package crew are fetched only after the crew have been retrieved.
    void getCrewAction().then(() => {
      void getCrewGroupsAction();
      void fetchPackageCrew();
    });
  }, [getCrewAction, getCrewGroupsAction, fetchPackageCrew]);

  // Crew in the store that belong to any selected group.
  /**
   * Crew a build would draw from the selected groups.
   *
   * Inactive crew are excluded to match what the build actually does. Counting them here would
   * preview additions that never happen, leaving the mismatch warning standing however many times
   * the crew is built, and would hide that a build removes an inactive member already in the
   * package.
   */
  const crewInSelectedGroups: ICrewMember[] = useMemo(() => {
    if (!crew?.length || !crewGroups?.length) return [];
    return crew.filter(
      (member) => member?.crewInfo?.isActive && member?.crewInfo?.groups?.some((group) => crewGroups.includes(group))
    );
  }, [crew, crewGroups]);

  const crewIdsInPackage: string[] = useMemo(() => packageCrew.map((row) => String(row.userId)), [packageCrew]);
  const crewIdsInSelectedGroups: string[] = useMemo(
    () => crewInSelectedGroups.map((member) => String(member._id)),
    [crewInSelectedGroups]
  );

  // True once the selected groups differ from the saved ones.
  const isDirty: boolean = useMemo(
    () => !_.isEqual(sortedCopy(crewGroups), sortedCopy(initialCrewGroups)),
    [crewGroups, initialCrewGroups]
  );

  /**
   * Whether both crew lists are in hand. Until they are, the package looks like it contains nobody
   * from its groups, which would flag every row for removal and put that count in front of the
   * admin in the build confirmation.
   */
  const crewDataReady: boolean = useMemo(
    // Crew already in the store count as loaded: the fetch is skipped entirely in that case.
    () => !loadingCrew && !loadingPackageCrew && (crewFetched || !!crew.length),
    [loadingCrew, loadingPackageCrew, crewFetched, crew.length]
  );

  const inactiveCrewInPackageWarning: string = useMemo(() => {
    const inactiveCrew = packageCrew.filter((row) => row.isActive === false);
    return inactiveCrew.length ? "Inactive crew in package will not be able to bid." : "";
  }, [packageCrew]);

  // Crew in the selected groups that a rebuild would add, and package crew it would remove.
  const crewInGroupsButNotPackage: string[] = useMemo(() => {
    if (!crewDataReady) return [];
    const inPackage = new Set(crewIdsInPackage);
    return crewIdsInSelectedGroups.filter((userId) => !inPackage.has(userId));
  }, [crewDataReady, crewIdsInPackage, crewIdsInSelectedGroups]);

  const crewInPackageButNotGroups: string[] = useMemo(() => {
    if (!crewDataReady) return [];
    const inGroups = new Set(crewIdsInSelectedGroups);
    return crewIdsInPackage.filter((userId) => !inGroups.has(userId));
  }, [crewDataReady, crewIdsInPackage, crewIdsInSelectedGroups]);

  const crewMismatchWarning: string = useMemo(() => {
    // Both the store crew and the package crew have to be in hand before a difference between them
    // is real rather than a half-loaded state.
    if (!crewDataReady) {
      return "";
    }
    switch (true) {
      case isDirty:
        return "Crew Groups have been modified. Save the package, then build the crew to apply them.";
      case !isDirty && !!crewInGroupsButNotPackage?.length && !!crewInPackageButNotGroups?.length:
        return `${crewInPackageButNotGroups?.length} crew member(s) found in package that don't exist in selected groups. ${crewInGroupsButNotPackage?.length} crew member(s) found in selected groups that aren't included in current package. ${inactiveCrewInPackageWarning}`;
      case !isDirty && !!crewInPackageButNotGroups?.length:
        return `${crewInPackageButNotGroups?.length} crew member(s) found in package that don't exist in selected groups. ${inactiveCrewInPackageWarning}`;
      case !isDirty && !!crewInGroupsButNotPackage?.length:
        return `${crewInGroupsButNotPackage?.length} crew member(s) found in selected groups that aren't included in current package. ${inactiveCrewInPackageWarning}`;
      case !isDirty && !!inactiveCrewInPackageWarning?.length:
        return inactiveCrewInPackageWarning;
      default:
        return "";
    }
  }, [crewInGroupsButNotPackage, crewInPackageButNotGroups, isDirty, inactiveCrewInPackageWarning, crewDataReady]);

  // Preview rows for crew a rebuild would add, built from the store's crew records.
  const crewBeingAddedToPackage: IVacationBidCrewListItem[] = useMemo(() => {
    const added = new Set(crewInGroupsButNotPackage);
    return crewInSelectedGroups
      .filter((member) => added.has(String(member._id)))
      .map((member) => ({
        vacationBidPackageId: packageId,
        userId: member._id,
        firstName: member.firstName,
        lastName: member.lastName,
        employeeId: member.employeeId,
        groupMembership: member.crewInfo?.groups ?? [],
        isActive: member.crewInfo?.isActive,
        vacationDays: 0,
        statDays: 0,
        pendingChange: "willBeAdded",
        createdBy: undefined,
        createdAt: undefined,
      }));
  }, [crewInGroupsButNotPackage, crewInSelectedGroups, packageId]);

  // Package rows flagged against the selected groups, followed by the preview rows.
  const tableRows: IVacationBidCrewListItem[] = useMemo(() => {
    if (!crewDataReady) return packageCrew;
    const removed = new Set(crewInPackageButNotGroups);
    const flagged: IVacationBidCrewListItem[] = packageCrew.map((row) => ({
      ...row,
      pendingChange: removed.has(String(row.userId)) ? "willBeRemoved" : "unchanged",
    }));
    return [...flagged, ...crewBeingAddedToPackage];
  }, [crewDataReady, packageCrew, crewInPackageButNotGroups, crewBeingAddedToPackage]);

  // BUILD FROM GROUPS / FILE IMPORT
  const [busy, setBusy] = useState<boolean>(false);
  const [buildOpen, setBuildOpen] = useState<boolean>(false);
  const crewFileInputRef = useRef<HTMLInputElement | null>(null);
  // The chosen file's rows and what the server says importing them would do, held while the admin
  // confirms.
  const [crewImport, setCrewImport] = useState<{
    fileName: string;
    rows: IVacationBidCrewImportRow[];
    issues: ICsvIssue[];
    fatal?: string;
    preview?: IVacationBidCrewImportRowPreview[];
  } | null>(null);

  const handleBuildCrew = useCallback(async (): Promise<void> => {
    if (!packageId) return;
    setBuildOpen(false);
    setBusy(true);
    try {
      const notifications: INotifications = {
        dispatch,
        successMessage: "Crew built from groups",
        errorMessage: "Unable to build the crew from groups",
      };
      if (await buildVacationBidCrew(packageId, notifications)) {
        await fetchPackageCrew();
      }
    } finally {
      if (isMounted.current) setBusy(false);
    }
  }, [packageId, dispatch, fetchPackageCrew]);

  const handleCrewFileChosen = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      const file = event.target.files?.[0];
      // Cleared so that choosing the same file twice in a row still raises a change event.
      event.target.value = "";
      if (!file || !packageId) return;

      const parsed = await parseCsvFile(file);
      const result = crewFromCsv(parsed);
      // The row objects carry no line number, so the file's own rows supply it for reporting.
      const lines = new Map(parsed.rows.map((row) => [row.values["employeeid"], row.line]));

      if (result.fatal || !result.rows.length) {
        setCrewImport({ fileName: file.name, rows: [], issues: result.issues, fatal: result.fatal });
        return;
      }

      setBusy(true);
      try {
        const preview = await previewVacationBidCrewImport(packageId, result.rows, {
          dispatch,
          errorMessage: "Unable to check the crew file",
        });
        if (!isMounted.current) return;
        if (!preview) {
          setCrewImport({
            fileName: file.name,
            rows: [],
            issues: result.issues,
            fatal: "The file could not be checked against the crew list.",
          });
          return;
        }

        // Rows the server cannot apply join the reader's own, so every skipped line is in one list.
        const unusable = preview
          .filter(
            (row) =>
              row.outcome !== VacationBidCrewImportOutcome.Update && row.outcome !== VacationBidCrewImportOutcome.Add
          )
          .map((row) => ({
            line: lines.get(row.employeeId) ?? 0,
            message: `${row.employeeId}${row.lastName ? ` (${row.firstName ?? ""} ${row.lastName})` : ""}: ${
              IMPORT_OUTCOME_REASON[row.outcome] ?? "cannot be imported."
            }`,
          }));

        setCrewImport({
          fileName: file.name,
          rows: result.rows,
          issues: [...result.issues, ...unusable].sort((a, b) => a.line - b.line),
          preview,
        });
      } finally {
        if (isMounted.current) setBusy(false);
      }
    },
    [packageId, dispatch]
  );

  // Exports the crew the package holds - not the preview rows, which are not in it, and not
  // narrowed by the table's own filters.
  const handleExportCrew = useCallback(() => {
    download(vacationCsvFileName(packageTitle, "crew"), crewToCsv(packageCrew));
  }, [packageTitle, packageCrew]);

  const handleConfirmCrewImport = useCallback(async (): Promise<void> => {
    if (!packageId || !crewImport?.rows.length) return;
    const rows = crewImport.rows;
    setCrewImport(null);
    setBusy(true);
    try {
      const applied = await importVacationBidCrew(packageId, rows, {
        dispatch,
        errorMessage: "Unable to import the crew file",
      });
      if (applied) {
        const adjustments = awardAdjustmentsLine(applied);
        dispatch(
          openSnackbar(
            awardChangesSnackbar(
              ["Crew entitlements imported.", ...(adjustments ? [adjustments] : [])],
              !!applied.flagged
            )
          )
        );
        await fetchPackageCrew();
      }
    } finally {
      if (isMounted.current) setBusy(false);
    }
  }, [packageId, crewImport, dispatch, fetchPackageCrew]);

  // MANUAL ADD / REMOVE
  const [addCandidate, setAddCandidate] = useState<ICrewMember | null>(null);
  const [removeTarget, setRemoveTarget] = useState<IVacationBidCrewListItem | null>(null);

  /** Active crew not already in the package, for the add picker. */
  const addableCrew: ICrewMember[] = useMemo(() => {
    const inPackage = new Set(crewIdsInPackage);
    return (crew ?? [])
      .filter((member) => member?.crewInfo?.isActive && !inPackage.has(String(member._id)))
      .sort((a, b) =>
        `${a.lastName ?? ""}${a.firstName ?? ""}`.localeCompare(`${b.lastName ?? ""}${b.firstName ?? ""}`)
      );
  }, [crew, crewIdsInPackage]);

  const handleAddCrew = useCallback(async (): Promise<void> => {
    if (!packageId || !addCandidate) return;
    setBusy(true);
    try {
      const added = await addVacationBidCrew(
        packageId,
        String(addCandidate._id),
        { vacationDays: 0, statDays: 0 },
        { dispatch, successMessage: "Crew member added", errorMessage: "Unable to add that crew member" }
      );
      if (added) {
        if (isMounted.current) setAddCandidate(null);
        await fetchPackageCrew();
      }
    } finally {
      if (isMounted.current) setBusy(false);
    }
  }, [packageId, addCandidate, dispatch, fetchPackageCrew]);

  const handleRemoveCrew = useCallback(async (): Promise<void> => {
    if (!packageId || !removeTarget) return;
    const userId = String(removeTarget.userId);
    setRemoveTarget(null);
    setBusy(true);
    try {
      const removed = await removeVacationBidCrew(packageId, userId, {
        dispatch,
        successMessage: "Crew member removed",
        errorMessage: "Unable to remove that crew member",
      });
      if (removed) await fetchPackageCrew();
    } finally {
      if (isMounted.current) setBusy(false);
    }
  }, [packageId, removeTarget, dispatch, fetchPackageCrew]);

  /** What confirming the open import would do, counted from the server's own classification. */
  const crewImportSummary = useMemo<string[]>(() => {
    if (!crewImport) return [];

    const preview = crewImport.preview;
    // No preview means the reader rejected the file before the server ever saw it. A fatal speaks
    // for itself; otherwise the issue list needs a statement above it like the other paths have.
    if (!preview) return crewImport.fatal ? [] : ["No crew rows could be read from this file."];
    const count = (outcome: VacationBidCrewImportOutcome): number =>
      preview.filter((row) => row.outcome === outcome).length;

    const updated = count(VacationBidCrewImportOutcome.Update);
    const added = count(VacationBidCrewImportOutcome.Add);
    const skipped = preview.length - updated - added;

    if (!updated && !added) return ["No rows in this file can be applied to this package."];

    const lines: string[] = [];
    if (updated)
      lines.push(
        `${updated} crew member${updated === 1 ? "" : "s"} already in the package will have their entitlements replaced.`
      );
    if (added) lines.push(`${added} crew member${added === 1 ? "" : "s"} will be added to the package.`);
    if (skipped) lines.push(`${skipped} row${skipped === 1 ? "" : "s"} cannot be applied and will be skipped.`);
    const changingAwards = preview.filter((row) => row.adjustments?.length).length;
    if (changingAwards) {
      lines.push(
        `${changingAwards} crew member${changingAwards === 1 ? "'s" : "s'"} new days change their awards. ` +
          "Blocks that only need resizing are resized; anything else is left as a warning in Award Bids."
      );
    }
    lines.push("Crew the file does not list are left untouched.");
    return lines;
  }, [crewImport]);

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      className="vacation_crew_groups_field"
      sx={(theme) => VacationCrewGroupsFieldStyles(theme).root}
    >
      <Typography className="required_fields_label">required fields</Typography>
      <Grid
        container
        direction="row"
        className="required_fields"
        alignItems="flex-start"
        alignContent="flex-start"
        style={{ width: "100%" }}
      >
        <Grid container>
          <FormikSelectField
            className="field selectfield crewgroups"
            label="Crew Groups"
            name="crewGroups"
            format={(groups: string[] | undefined) => (groups?.length ? groups.map(toGroupOption) : [])}
            parse={(options: IGroupOption[] | undefined) =>
              options?.length ? options.map((option) => option.key) : []
            }
            options={groupOptions}
            isMulti={true}
            isClearable={true}
            menuPosition="fixed"
            maxMenuHeight={400}
            menuPortalTarget={mosaicPane}
            multiMode="checkbox"
            placeholder={Array.isArray(crewGroups) && crewGroups.length ? crewGroups.join(", ") : "Select..."}
            data-testid={VACATION_BID_TEST_ID.crewGroupsSelect}
          />
          <Button
            className="refresh_btn"
            variant="outlined"
            color="primary"
            onClick={handleRefresh}
            data-testid={VACATION_BID_TEST_ID.refreshCrewButton}
          >
            Refresh Crew & Groups
          </Button>
        </Grid>
      </Grid>

      <Grid container alignItems="center" wrap="nowrap" className="crew_actions">
        <Button
          className="build_crew_btn"
          variant="outlined"
          color="primary"
          disabled={!packageId || isDirty || busy || !crewDataReady}
          onClick={() => setBuildOpen(true)}
          data-testid={VACATION_BID_TEST_ID.buildCrewButton}
        >
          Build Crew From Groups
        </Button>
        <Button
          className="import_csv_btn"
          variant="outlined"
          disabled={!packageId || busy}
          onClick={() => crewFileInputRef.current?.click()}
          data-testid={VACATION_BID_TEST_ID.importCrewCsvButton}
        >
          Import Vacation/Stat Days (CSV)
        </Button>
        <Tooltip title="Downloads every crew member in this package, whatever the filters show" placement="top">
          <span>
            <Button
              className="import_csv_btn"
              variant="outlined"
              disabled={!packageCrew.length}
              onClick={handleExportCrew}
              data-testid={VACATION_BID_TEST_ID.exportCrewCsvButton}
            >
              Export Vacation/Stat Days (CSV)
            </Button>
          </span>
        </Tooltip>
        <Autocomplete
          className="add_crew_select"
          size="small"
          options={addableCrew}
          value={addCandidate}
          onChange={(_event, value) => setAddCandidate(value)}
          getOptionLabel={(member) =>
            `${member.lastName ?? ""}, ${member.firstName ?? ""}${member.employeeId ? ` (${member.employeeId})` : ""}`
          }
          isOptionEqualToValue={(option, value) => String(option._id) === String(value._id)}
          disabled={!packageId || busy}
          renderInput={(params) => <MuiTextField {...params} label="Add Crew Member" variant="standard" />}
          data-testid={VACATION_BID_TEST_ID.addCrewSelect}
        />
        <Button
          className="add_crew_btn"
          variant="outlined"
          color="primary"
          disabled={!packageId || !addCandidate || busy}
          onClick={() => {
            void handleAddCrew();
          }}
          data-testid={VACATION_BID_TEST_ID.addCrewButton}
        >
          Add
        </Button>
        <input
          type="file"
          accept=".csv,text/csv"
          hidden
          ref={crewFileInputRef}
          onChange={(event) => {
            void handleCrewFileChosen(event);
          }}
          data-testid={VACATION_BID_TEST_ID.importCrewCsvInput}
        />
      </Grid>

      <VacationCrewTable
        id={id}
        path={path}
        packageId={packageId}
        rows={tableRows}
        crewAdded={crewInGroupsButNotPackage}
        crewRemoved={crewInPackageButNotGroups}
        onRowUpdated={handleRowUpdated}
        onRemoveCrew={setRemoveTarget}
        loading={loadingPackageCrew || loadingCrew || busy}
      />

      {!!crewMismatchWarning?.length && (
        <Grid container alignItems="flex-start" wrap="nowrap" className="crew_warning">
          <WarningIcon color="secondary" fontSize="small" />
          <Typography variant="caption" color="secondary" className="crew_mismatch_warning">
            {crewMismatchWarning}
          </Typography>
        </Grid>
      )}

      <Dialog open={buildOpen} onClose={() => setBuildOpen(false)} data-testid={VACATION_BID_TEST_ID.buildCrewDialog}>
        <DialogTitle>Build Crew From Groups</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {`This replaces the package's crew list with the members of ${
              crewGroups?.length ? crewGroups.join(", ") : "the selected groups"
            }.`}
          </Typography>
          <Typography variant="body2">
            {crewInPackageButNotGroups.length
              ? `${crewInPackageButNotGroups.length} crew member(s) currently in the package are not in those groups and will be removed, including any added from a file or by hand.`
              : "No crew currently in the package would be removed."}
          </Typography>
          <Typography variant="body2">Entitlements are kept for crew who remain.</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={() => setBuildOpen(false)}
            data-testid={VACATION_BID_TEST_ID.buildCrewCancelButton}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => {
              void handleBuildCrew();
            }}
            data-testid={VACATION_BID_TEST_ID.buildCrewConfirmButton}
          >
            Build Crew
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        data-testid={VACATION_BID_TEST_ID.removeCrewDialog}
      >
        <DialogTitle>Remove Crew Member</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {`Remove ${removeTarget?.firstName ?? ""} ${removeTarget?.lastName ?? ""} from this package, along with the vacation and stat days held for them in it?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" variant="contained" color="secondary" onClick={() => setRemoveTarget(null)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => {
              void handleRemoveCrew();
            }}
            data-testid={VACATION_BID_TEST_ID.removeCrewConfirmButton}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <ImportCsvDialog
        open={!!crewImport}
        title="Import Vacation/Stat Days"
        fileName={crewImport?.fileName ?? ""}
        summary={crewImportSummary}
        issues={crewImport?.issues ?? []}
        fatal={crewImport?.fatal}
        confirmLabel="Import Entitlements"
        confirmDisabled={
          !crewImportSummary.length ||
          !crewImport?.preview?.some(
            (row) =>
              row.outcome === VacationBidCrewImportOutcome.Update || row.outcome === VacationBidCrewImportOutcome.Add
          )
        }
        onConfirm={() => {
          void handleConfirmCrewImport();
        }}
        onClose={() => setCrewImport(null)}
      />
    </Grid>
  );
};
